import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { PictureElement } from '@/pictureElement/Picture';
import { usePostContentContext } from '@/postContentElement/context';
import { SECTION_DEFAULTS } from '@/sectionElement/Section.constants';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

import { useAnchorable } from '../../hooks/useAnchorable';

import { genericEmbed, genericEmbedChildrenContainer, genericEmbedContainer } from './GenericEmbed.css';
import type { TPostGenericEmbedProps } from './GenericEmbed.types';

// The column width is set by an ancestor Section or Container that the card can't read, so size for the widest
// default column. Not sizes "auto": its UA style lays the unsized img out at 300x150.
const POST_COLUMN_MAX_WIDTH_PX = Number.parseInt(SECTION_DEFAULTS.innerWidthMax, 10);
const TAILWIND_SM_BREAKPOINT_PX = 640;
// fit=scale-down returns the original for widths above it. No 2160: an AVIF variant near the original's width
// can outweigh the original.
const LINK_CARD_IMAGE_WIDTHS = [360, 720, 1080, 1440, 2880];
const GIF_PATH_PATTERN = /\.gif(?:[?#]|$)/i;

export const PostGenericEmbed = ({
  element,
  children,
  editorMode,
  imageSlot,
  rewriteImageSrc,
  isIndexingDisabled,
}: TPostGenericEmbedProps) => {
  const { variant, url, target, imageUrl, imageWidthPercentage, imageVerticalAlign } = element.attrs;
  const { anchorAttributes } = useAnchorable(element.attrs);
  const { utmParams } = usePostContentContext();
  const urlWithUTMParams = evaluateAndAppendUTMParams(url, utmParams);

  const alteredImageUrl = imageUrl && rewriteImageSrc ? rewriteImageSrc(imageUrl) : imageUrl;
  const sanitizedImageWidthPercentage =
    typeof imageWidthPercentage === 'number' && Number.isFinite(imageWidthPercentage)
      ? Math.max(20, Math.min(60, Math.round(imageWidthPercentage)))
      : 40;

  // Only beehiiv CDN transform URLs accept a width; a resized GIF variant is larger than the original.
  const isResizable = Boolean(alteredImageUrl?.includes('/cdn-cgi/image/')) && !GIF_PATH_PATTERN.test(imageUrl ?? '');
  const imageSrcSet =
    isResizable && alteredImageUrl?.includes('format=auto')
      ? LINK_CARD_IMAGE_WIDTHS.map(
          (width) => `${alteredImageUrl.replace('format=auto', `format=auto,width=${width}`)} ${width}w`,
        ).join(', ')
      : undefined;
  const imageSizes =
    imageSrcSet &&
    (variant === 'image-top-text-bottom'
      ? `(min-width: ${POST_COLUMN_MAX_WIDTH_PX}px) ${POST_COLUMN_MAX_WIDTH_PX}px, 100vw`
      : `(min-width: ${TAILWIND_SM_BREAKPOINT_PX}px) ${Math.ceil((POST_COLUMN_MAX_WIDTH_PX * sanitizedImageWidthPercentage) / 100)}px, 100vw`);

  const imageAlignSelf =
    imageVerticalAlign === 'top' ? 'flex-start' : imageVerticalAlign === 'bottom' ? 'flex-end' : 'center';

  const imageMarkup = imageSlot ?? (
    // The picture is a flex item in the side layouts, so it needs w-full for the img to fill the wrapper.
    <PictureElement src={isResizable ? alteredImageUrl : undefined} className="w-full">
      <img
        src={alteredImageUrl}
        srcSet={imageSrcSet}
        sizes={imageSizes}
        className="w-full h-auto object-cover"
        alt=""
      />
    </PictureElement>
  );

  const content = (
    <div
      className={genericEmbed}
      style={
        {
          '--generic-embed-image-width': `${sanitizedImageWidthPercentage}%`,
        } as CSSProperties
      }
    >
      {variant === 'image-top-text-bottom' && (
        <div data-generic-embed-image-wrapper data-generic-embed-image-top className="pb-[12px]">
          {imageMarkup}
        </div>
      )}

      <div
        data-generic-embed-layout
        className={classNames(
          'border-collapse w-full flex sm:flex-row',
          variant === 'image-left-text-right' ? 'flex-col' : 'flex-col-reverse',
        )}
      >
        {variant === 'image-left-text-right' && (
          <div
            data-generic-embed-image-wrapper
            data-generic-embed-image-left
            className="w-full mb-[20px] sm:mb-0 sm:mr-[20px] sm:w-[var(--generic-embed-image-width)] sm:basis-[var(--generic-embed-image-width)] flex"
            style={{ alignSelf: imageAlignSelf }}
          >
            {imageMarkup}
          </div>
        )}

        <div className={genericEmbedChildrenContainer}>{children}</div>

        {variant === 'text-left-image-right' && (
          <div
            data-generic-embed-image-wrapper
            data-generic-embed-image-right
            className="w-full mb-[20px] sm:mb-0 sm:ml-[20px] sm:w-[var(--generic-embed-image-width)] sm:basis-[var(--generic-embed-image-width)] flex"
            style={{ alignSelf: imageAlignSelf }}
          >
            {imageMarkup}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div {...anchorAttributes} data-generic-embed-container className={genericEmbedContainer}>
      {editorMode ? (
        content
      ) : (
        <a
          href={urlWithUTMParams}
          target={target === '_self' ? undefined : '_blank'}
          rel={isIndexingDisabled ? 'noreferrer' : undefined}
        >
          {content}
        </a>
      )}
    </div>
  );
};
